import { getRuleValue, type RuleOverrides } from "../../data/ruleRegistry";
import { lookupHeaderDepth } from "../framing/headerRules";
import {
	STUD_D_M,
	STUD_W_M,
	INTERIOR_LINING_D_M,
	PANEL_T_M,
	resolveWallHeightM,
} from "../geometry/buildingDimensions";
import { clampPartitionSplit } from "../partition/partitionSplit";
import { createRectRoofGeometry } from "../roof/rectRoofGeometry";
import type { RoofConfig, SaunaDimensions } from "../../engines/types";
import type { DoorHingeSide, DoorSwing } from "../framing/generateFramedWall";

export type OpeningWall = "front" | "back" | "left" | "right" | "divider";
export type OpeningType = "door" | "window";

export interface OpeningLike {
	id: string;
	wall: OpeningWall;
	type: OpeningType;
	xMm: number;
	yMm: number;
	widthMm: number;
	heightMm: number;
	glazed?: boolean;
	hingeSide?: DoorHingeSide;
	swing?: DoorSwing;
}

export type OpeningValidationCode =
	| "invalid_size"
	| "out_of_bounds_start"
	| "out_of_bounds_end"
	| "out_of_bounds_height"
	| "overlap"
	| "framing_clearance"
	| "header_clearance"
	| "partition_collision";

export interface OpeningValidationIssue {
	code: "out_of_bounds" | "invalid_size" | "overlap";
	detail: OpeningValidationCode;
	message: string;
	params?: { wallLengthMm?: number; wallHeightMm?: number };
	openingId?: string;
	relatedOpeningId?: string;
}

export interface OpeningValidationResult {
	valid: boolean;
	issues: OpeningValidationIssue[];
}

interface ValidateWallOpeningsArgs {
	checkFraming?: boolean;
	heightAtXMm?: (xMm: number) => number;
	openings: OpeningLike[];
	wallLengthMm: number;
	wallHeightMm?: number;
	/**
	 * Explicit horizontal edge clearance override, in mm. When omitted, the
	 * effective clearance is resolved per-opening from `defaultEdgeClearanceMm`
	 * (exterior walls reserve corners/trim; divider openings reserve the
	 * exterior wall depth and door support).
	 */
	minEdgeClearanceMm?: number;
	overrides?: RuleOverrides;
}

interface ValidateOpeningSetArgs {
	partitionPositionMm?: number;
	roofConfig?: RoofConfig;
	hasRelaxRoom?: boolean;
	saunaPosition?: "left" | "right";
	openings: OpeningLike[];
	dimensions: SaunaDimensions;
	partitionSplit?: number;
	/** Explicit wall height, mm. Wins over `wallHeightM`. */
	wallHeightMm?: number;
	/** The design's wall height (issue 155); absent = the rule default. */
	wallHeightM?: number;
	minEdgeClearanceMm?: number;
	overrides?: RuleOverrides;
}

/**
 * Interior partition ("mellanvägg") openings have no exterior trim (foder) or
 * corner boards (knutbrädor) to collide with, so they keep the pre-existing
 * flat clearance instead of picking up the exterior corner-clearance rule.
 */
export const DIVIDER_EDGE_CLEARANCE_MM = (STUD_D_M + 2 * STUD_W_M) * 1000;

/**
 * Resolves the horizontal edge clearance that applies when a caller doesn't
 * pass an explicit `minEdgeClearanceMm`. Exterior walls (front/back/left/
 * right) read `framing.minCornerClearanceMm` — the same rule
 * `OpeningDragController` enforces at drag time — so the trim/knutbräda
 * collision limit has a single source of truth. Divider walls stay at the
 * old flat minimum.
 */
function defaultEdgeClearanceMm(
	wall: OpeningWall,
	overrides: RuleOverrides,
): number {
	if (wall === "divider") return DIVIDER_EDGE_CLEARANCE_MM;
	return getRuleValue<number>("framing.minCornerClearanceMm", overrides);
}

function hasOverlap(a: OpeningLike, b: OpeningLike): boolean {
	if (a.wall !== b.wall) return false;
	const xOverlap = a.xMm < b.xMm + b.widthMm && a.xMm + a.widthMm > b.xMm;
	const yOverlap = a.yMm < b.yMm + b.heightMm && a.yMm + a.heightMm > b.yMm;
	return xOverlap && yOverlap;
}

export function validateWallOpenings({
	openings,
	wallLengthMm,
	wallHeightMm = 2100,
	minEdgeClearanceMm,
	overrides = {},
	checkFraming = true,
	heightAtXMm,
}: ValidateWallOpeningsArgs): OpeningValidationIssue[] {
	const issues: OpeningValidationIssue[] = [];

	for (const opening of openings) {
		if (
			![opening.xMm, opening.yMm, opening.widthMm, opening.heightMm].every(
				Number.isFinite,
			) ||
			opening.widthMm <= 0 ||
			opening.heightMm <= 0
		) {
			issues.push({
				code: "invalid_size",
				detail: "invalid_size",
				openingId: opening.id,
				message: "Bredd och höjd måste vara större än 0.",
			});
			continue;
		}

		const clearanceMm =
			minEdgeClearanceMm ?? defaultEdgeClearanceMm(opening.wall, overrides);

		if (opening.xMm < clearanceMm || opening.yMm < 0) {
			issues.push({
				code: "out_of_bounds",
				detail: "out_of_bounds_start",
				openingId: opening.id,
				message: "Öppning måste starta inom väggens tillåtna område.",
			});
		}

		if (opening.xMm + opening.widthMm > wallLengthMm - clearanceMm) {
			// The bound is the wall length MINUS the corner clearance — report
			// that limit, not the raw wall length. An opening can be well inside
			// the wall and still trip this check; telling the user it "extends
			// beyond the wall (2500 mm)" pointed them at the wrong constraint.
			const maxXMm = wallLengthMm - clearanceMm;
			issues.push({
				code: "out_of_bounds",
				detail: "out_of_bounds_end",
				openingId: opening.id,
				params: { wallLengthMm: maxXMm },
				message: `Öppning går förbi väggens tillåtna område (max ${maxXMm} mm inkl. hörnavstånd ${clearanceMm} mm).`,
			});
		}

		const availableHeight = heightAtXMm
			? Math.min(
					heightAtXMm(opening.xMm - STUD_W_M * 1000),
					heightAtXMm(opening.xMm + opening.widthMm + STUD_W_M * 1000),
				)
			: wallHeightMm;
		const requiredHeader =
			opening.wall === "divider"
				? STUD_W_M * 1000
				: lookupHeaderDepth(opening.widthMm, overrides).depthMm;
		if (
			checkFraming &&
			opening.yMm + opening.heightMm + requiredHeader + STUD_W_M * 1000 >
				availableHeight
		) {
			issues.push({
				code: "out_of_bounds",
				detail: "header_clearance",
				openingId: opening.id,
				message:
					"Det finns inte plats för överstycke och hammarband ovanför öppningen.",
			});
		}
		if (opening.yMm + opening.heightMm > availableHeight) {
			issues.push({
				code: "out_of_bounds",
				detail: "out_of_bounds_height",
				openingId: opening.id,
				params: { wallHeightMm },
				message: `Öppning går över väggens höjd (${wallHeightMm} mm).`,
			});
		}
	}

	for (let i = 0; i < openings.length; i += 1) {
		for (let j = i + 1; j < openings.length; j += 1) {
			const a = openings[i];
			const b = openings[j];
			if (checkFraming && a.wall === b.wall && !hasOverlap(a, b)) {
				// Each opening currently generates full-height jambs independently.
				// Stacking needs a shared frame design; vertical separation alone cannot prevent overlap.
				const gapX =
					Math.max(a.xMm, b.xMm) -
					Math.min(a.xMm + a.widthMm, b.xMm + b.widthMm);
				if (gapX < 3 * STUD_W_M * 1000) {
					for (const [op, other] of [
						[a, b],
						[b, a],
					])
						issues.push({
							code: "overlap",
							detail: "framing_clearance",
							openingId: op.id,
							relatedOpeningId: other.id,
							message: "Öppningarnas regelverk behöver mer utrymme.",
						});
				}
			}
			if (!hasOverlap(a, b)) continue;
			issues.push({
				code: "overlap",
				detail: "overlap",
				openingId: a.id,
				relatedOpeningId: b.id,
				message: "Öppningen överlappar en annan öppning på samma vägg.",
			});
			issues.push({
				code: "overlap",
				detail: "overlap",
				openingId: b.id,
				relatedOpeningId: a.id,
				message: "Öppningen överlappar en annan öppning på samma vägg.",
			});
		}
	}

	return issues;
}

function wallLengthMm(wall: OpeningWall, dimensions: SaunaDimensions): number {
	if (wall === "divider") {
		return Math.round(dimensions.width * 1000);
	}

	if (wall === "front" || wall === "back") {
		return Math.round(dimensions.width * 1000);
	}
	return Math.round(dimensions.length * 1000);
}

export function validateOpeningSet({
	openings,
	dimensions,
	partitionSplit,
	partitionPositionMm,
	roofConfig,
	hasRelaxRoom,
	saunaPosition,
	wallHeightM,
	wallHeightMm = Math.round(resolveWallHeightM(wallHeightM) * 1000),
	minEdgeClearanceMm,
	overrides = {},
}: ValidateOpeningSetArgs): OpeningValidationResult {
	const grouped = new Map<OpeningWall, OpeningLike[]>();
	for (const opening of openings) {
		const group = grouped.get(opening.wall) ?? [];
		group.push(opening);
		grouped.set(opening.wall, group);
	}

	const issues: OpeningValidationIssue[] = [];
	const roof =
		roofConfig && roofConfig.kind !== "barrel"
			? createRectRoofGeometry({
					width: dimensions.width,
					length: dimensions.length,
					wallHeight: wallHeightMm / 1000,
					roofConfig,
				})
			: undefined;
	const hasPartition = hasRelaxRoom ?? partitionSplit !== undefined;
	const split = clampPartitionSplit(dimensions.length, partitionSplit ?? 0.5);
	const position =
		partitionPositionMm ??
		dimensions.length * 1000 * (saunaPosition === "right" ? 1 - split : split);
	const partitionHalfWidth =
		(STUD_D_M / 2 + INTERIOR_LINING_D_M + PANEL_T_M) * 1000;
	if (hasPartition)
		for (const op of openings) {
			if (
				(op.wall === "left" || op.wall === "right") &&
				op.xMm < position + partitionHalfWidth &&
				op.xMm + op.widthMm > position - partitionHalfWidth
			) {
				issues.push({
					code: "overlap",
					detail: "partition_collision",
					openingId: op.id,
					message:
						"Öppningen korsar mellanväggens anslutning. Flytta öppningen eller mellanväggen.",
				});
			}
		}
	for (const [wall, wallOpenings] of grouped.entries()) {
		issues.push(
			...validateWallOpenings({
				openings: wallOpenings,
				wallLengthMm: wallLengthMm(wall, dimensions),
				wallHeightMm,
				heightAtXMm: !roof
					? undefined
					: (axis) => {
							if (roofConfig?.kind === "gable" && wall !== "divider")
								return wallHeightMm;
							const x =
								wall === "left"
									? -dimensions.width / 2
									: wall === "right"
										? dimensions.width / 2
										: axis / 1000 - dimensions.width / 2;
							const z =
								wall === "front"
									? dimensions.length / 2
									: wall === "back"
										? -dimensions.length / 2
										: wall === "divider"
											? position / 1000 - dimensions.length / 2
											: axis / 1000 - dimensions.length / 2;
							return roof.getRoofHeightAt(x, z) * 1000;
						},
				minEdgeClearanceMm,
				overrides,
			}),
		);
	}

	return {
		valid: issues.length === 0,
		issues,
	};
}
